/* ============================================================
   Страница чата между сотрудниками.
   Слева — список диалогов, справа — лента сообщений + поле ввода.
   Real-time через useChatSocket (входящие сообщения).
   ============================================================ */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Send, MessagesSquare, ArrowLeft, Paperclip, X, FileText } from 'lucide-react';
import { chatApi, getErrorMessage } from '@/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useChatSocket } from '@/hooks/useChatSocket';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTime } from '@/lib/format';
import type { ConversationListItem, ChatMessage, ChatUser, ChatAttachment } from '@/types';
import './chat.css';

export function ChatPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const activeIdRef = useRef<number | null>(null);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  // ── Загрузка списка диалогов ──
  const loadConversations = useCallback(async () => {
    try {
      const list = await chatApi.conversations();
      setConversations(list);
    } catch (e) {
      toast(getErrorMessage(e), 'error');
    } finally {
      setLoadingConvs(false);
    }
  }, [toast]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Загрузка сообщений выбранного диалога ──
  const openConversation = useCallback(async (id: number) => {
    setActiveId(id);
    setLoadingMsgs(true);
    try {
      const msgs = await chatApi.messages(id);
      setMessages(msgs);
      // Сбрасываем непрочитанные в списке
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c))
      );
    } catch (e) {
      toast(getErrorMessage(e), 'error');
    } finally {
      setLoadingMsgs(false);
    }
  }, [toast]);

  // ── Real-time: входящее сообщение ──
  const handleIncoming = useCallback((msg: ChatMessage) => {
    // Если сообщение из открытого диалога — добавляем в ленту (без дублей)
    if (msg.conversation === activeIdRef.current) {
      setMessages((prev) =>
        prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]
      );
      // и сразу помечаем прочитанным на сервере
      chatApi.markRead(msg.conversation).catch(() => {});
    }
    // Обновляем список диалогов: последнее сообщение + непрочитанные
    setConversations((prev) => {
      const idx = prev.findIndex((c) => c.id === msg.conversation);
      if (idx === -1) {
        // новый диалог — перезагрузим список
        loadConversations();
        return prev;
      }
      const updated = { ...prev[idx] };
      updated.last_message = {
        text: msg.text, sender_id: msg.sender, created_at: msg.created_at,
      };
      updated.updated_at = msg.created_at;
      if (msg.conversation !== activeIdRef.current && !msg.is_mine) {
        updated.unread_count += 1;
      }
      // поднимаем диалог наверх
      const rest = prev.filter((_, i) => i !== idx);
      return [updated, ...rest];
    });
  }, [loadConversations]);

  useChatSocket({ onMessage: handleIncoming });

  // ── Автопрокрутка ленты вниз ──
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight });
  }, [messages]);

  // ── Отправка сообщения (через REST), с вложениями ──
  const send = async () => {
    const text = input.trim();
    if ((!text && files.length === 0) || !activeId) return;
    setSending(true);
    try {
      const msg = await chatApi.sendMessage(activeId, text, files.length ? files : undefined);
      setInput('');
      setFiles([]);
      setMessages((prev) =>
        prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]
      );
    } catch (e) {
      toast(getErrorMessage(e), 'error');
    } finally {
      setSending(false);
    }
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const active = conversations.find((c) => c.id === activeId) || null;

  return (
    <AppLayout>
      <div className="chat">
        {/* Список диалогов */}
        <div className={`chat__list${activeId ? ' chat__list--hidden-mobile' : ''}`}>
          <div className="chat__list-head">
            <h2>Чат</h2>
            <Button size="sm" variant="primary" onClick={() => setNewOpen(true)}>
              <Plus size={15} /> Новый
            </Button>
          </div>
          {loadingConvs ? (
            <Spinner label="Загрузка…" />
          ) : conversations.length === 0 ? (
            <EmptyState
              icon={<MessagesSquare size={28} />}
              title="Диалогов пока нет"
              hint="Начни переписку с коллегой"
            />
          ) : (
            <div className="chat__convs">
              {conversations.map((c) => (
                <button
                  key={c.id}
                  className={`chat__conv${c.id === activeId ? ' chat__conv--active' : ''}`}
                  onClick={() => openConversation(c.id)}
                >
                  <div className="chat__conv-avatar">
                    {c.display_title.charAt(0).toUpperCase()}
                  </div>
                  <div className="chat__conv-body">
                    <div className="chat__conv-top">
                      <span className="chat__conv-title">{c.display_title}</span>
                      {c.unread_count > 0 && (
                        <span className="chat__badge">{c.unread_count}</span>
                      )}
                    </div>
                    <div className="chat__conv-preview">
                      {c.last_message?.text || 'Нет сообщений'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Лента сообщений */}
        <div className={`chat__thread${activeId ? '' : ' chat__thread--hidden-mobile'}`}>
          {!active ? (
            <div className="chat__placeholder">
              <MessagesSquare size={40} />
              <p>Выбери диалог или начни новый</p>
            </div>
          ) : (
            <>
              <div className="chat__thread-head">
                <button className="chat__back" onClick={() => setActiveId(null)}>
                  <ArrowLeft size={18} />
                </button>
                <span className="chat__thread-title">{active.display_title}</span>
              </div>

              <div className="chat__messages" ref={threadRef}>
                {loadingMsgs ? (
                  <Spinner label="Загрузка сообщений…" />
                ) : messages.length === 0 ? (
                  <div className="chat__empty-thread">Начни разговор первым сообщением</div>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`chat__msg${m.is_mine ? ' chat__msg--mine' : ''}`}
                    >
                      {!m.is_mine && active.kind === 'group' && (
                        <div className="chat__msg-sender">{m.sender_name}</div>
                      )}
                      {m.text && <div className="chat__msg-bubble">{m.text}</div>}
                      {m.attachments?.length > 0 && (
                        <div className="chat__msg-attachments">
                          {m.attachments.map((a) => <AttachmentView key={a.id} att={a} />)}
                        </div>
                      )}
                      <div className="chat__msg-time">{formatDateTime(m.created_at)}</div>
                    </div>
                  ))
                )}
              </div>

              <div className="chat__compose">
                {files.length > 0 && (
                  <div className="chat__pending">
                    {files.map((f, i) => (
                      <span key={i} className="chat__pending-item">
                        <FileText size={13} />
                        <span className="chat__pending-name">{f.name}</span>
                        <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                          <X size={13} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="chat__compose-row">
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={onPickFiles}
                  />
                  <button
                    className="chat__attach-btn"
                    title="Прикрепить файл"
                    onClick={() => fileRef.current?.click()}
                  >
                    <Paperclip size={18} />
                  </button>
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder="Написать сообщение…"
                    rows={1}
                  />
                  <Button
                    variant="primary"
                    disabled={(!input.trim() && files.length === 0) || sending}
                    onClick={send}
                  >
                    <Send size={16} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <NewChatModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(conv) => {
          setNewOpen(false);
          loadConversations();
          openConversation(conv.id);
        }}
        onError={(m) => toast(m, 'error')}
      />
    </AppLayout>
  );
}

function AttachmentView({ att }: { att: ChatAttachment }) {
  if (att.is_image) {
    return (
      <a href={att.url} target="_blank" rel="noreferrer" className="chat__att-image">
        <img src={att.url} alt={att.filename} />
      </a>
    );
  }
  const kb = att.size ? Math.max(1, Math.round(att.size / 1024)) : 0;
  return (
    <a href={att.url} target="_blank" rel="noreferrer" className="chat__att-file">
      <FileText size={18} />
      <span className="chat__att-file-info">
        <span className="chat__att-file-name">{att.filename}</span>
        {kb > 0 && <span className="chat__att-file-size">{kb} КБ</span>}
      </span>
    </a>
  );
}

function NewChatModal({
  open, onClose, onCreated, onError,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (conv: { id: number }) => void;
  onError: (msg: string) => void;
}) {
  const { data: contacts, isLoading } = useQuery({
    queryKey: ['chat-contacts'],
    queryFn: () => chatApi.contacts(),
    enabled: open,
  });
  const [creating, setCreating] = useState(false);

  const start = async (u: ChatUser) => {
    setCreating(true);
    try {
      const conv = await chatApi.createDirect(u.id);
      onCreated(conv);
    } catch (e) {
      onError(getErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Новый диалог">
      {isLoading ? (
        <Spinner label="Загрузка коллег…" />
      ) : !contacts || contacts.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>
          В компании пока нет других сотрудников. Пригласи коллег в разделе «Администрирование».
        </p>
      ) : (
        <div className="chat__contacts">
          {contacts.map((u) => (
            <button key={u.id} className="chat__contact" disabled={creating} onClick={() => start(u)}>
              <div className="chat__conv-avatar">{u.name.charAt(0).toUpperCase()}</div>
              <div>
                <div className="chat__contact-name">{u.name}</div>
                <div className="chat__contact-email">{u.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
